import { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, Plus, Trophy, X, Zap } from 'lucide-react';
import { SportCard } from './SportCard';
import { AddSportModal } from './AddSportModal';
import { AddTeamModal } from './AddTeamModal';
import { AddFundraiserModal } from './AddFundraiserModal';
import { EditFundraiserModal } from '../competitors/EditFundraiserModal';
import { useOrganizationStore } from '../../store/organizationStore';
import { useFundraiserStore } from '../../store/fundraiserStore';
import { usePersistedBoolean } from '../../hooks/usePersistedBoolean';
import { filterSportsByQuery } from '../../lib/sportFilters';
import { visibleSports, hiddenCounts, isHiddenTeam } from '../../lib/teamVisibility';
import { compareLevels } from '../../lib/levelRank';
import type { OrganizationWithPeople } from '../../types';
import { formatSportLabel } from '../../lib/sportLabel';
import { ConfirmDialog } from '../common/ConfirmDialog';
import api from '../../lib/api';
import toast from 'react-hot-toast';
import type { SportWithTeams, LinkedPerson, CompetitorFundraiser } from '../../types';

const SECTION_KEY = 'crm:orgPage:sportsSection:expanded';

interface SportsTeamsSectionProps {
  organization: OrganizationWithPeople;
  onLinkPerson?: (sportId: string | null, teamId: string | null) => void;
  onScheduleActivity?: (personId: string, personName: string) => void;
  onConvert?: (personId: string, teamId: string, sportId: string) => void;
  /**
   * Gates the Add Sport / Add Team / Add Person / Add Fundraiser mutation
   * affordances inside this section and within child SportCards.
   * Defaults to true to preserve existing callsites.
   */
  canEdit?: boolean;
  /** ADMIN-only: enables delete/unlink controls on CRM-only sports/teams/coaches. */
  canDelete?: boolean;
}

/**
 * Primary content section on the org detail page.
 * Shows the full sport → team → people hierarchy, with a collapsible
 * outer wrapper (persisted per-user in localStorage) and an in-section
 * sport-name filter when expanded.
 */
export const SportsTeamsSection = ({ organization, onLinkPerson, onScheduleActivity, onConvert, canEdit = true, canDelete = false }: SportsTeamsSectionProps) => {
  const { fetchOrganization } = useOrganizationStore();
  const { orgFundraisers, fetchOrgFundraisers } = useFundraiserStore();

  const blastSchoolId = organization.blastSchoolId;

  // Fetch org fundraisers for SportCard display
  useEffect(() => {
    fetchOrgFundraisers(organization.id);
  }, [organization.id, fetchOrgFundraisers]);

  // Collapsible state + in-section filter
  const [isExpanded, setIsExpanded] = usePersistedBoolean(SECTION_KEY, true);
  const [filterQuery, setFilterQuery] = useState('');
  const [showEmptyTeams, setShowEmptyTeams] = usePersistedBoolean('crm:orgPage:showEmptyTeams', false);

  // Modals
  const [isAddSportOpen, setIsAddSportOpen] = useState(false);
  const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
  const [addTeamSportId, setAddTeamSportId] = useState('');
  const [addTeamSportName, setAddTeamSportName] = useState('');
  const [isAddFundraiserOpen, setIsAddFundraiserOpen] = useState(false);
  const [addFundraiserSportId, setAddFundraiserSportId] = useState<string | null>(null);
  const [addFundraiserSportName, setAddFundraiserSportName] = useState('');
  const [editingFundraiser, setEditingFundraiser] = useState<CompetitorFundraiser | null>(null);

  // Admin delete/unlink orchestration (CRM-only). One ConfirmDialog for all three.
  type PendingDelete =
    | { kind: 'sport'; sportId: string; label: string }
    | { kind: 'team'; sportId: string; teamId: string; label: string }
    | { kind: 'coach'; personId: string; linkId: string; label: string };
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const handleRequestDeleteSport = (sport: SportWithTeams) => {
    setPendingDelete({ kind: 'sport', sportId: sport.id, label: formatSportLabel(sport.gender, sport.name) });
  };

  const handleRequestDeleteTeam = (team: SportWithTeams['teams'][number], sportId: string) => {
    setPendingDelete({ kind: 'team', sportId, teamId: team.id, label: team.level });
  };
  const handleRequestUnlinkCoach = (link: LinkedPerson) => {
    const p = link.person;
    const name = `${p.crmFirstName || p.firstName} ${p.crmLastName || p.lastName}`.trim();
    setPendingDelete({ kind: 'coach', personId: p.id, linkId: link.id, label: name });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      if (pendingDelete.kind === 'sport') {
        await api.delete(`/api/organizations/${organization.id}/sports/${pendingDelete.sportId}`);
        toast.success(`Deleted ${pendingDelete.label}`);
      } else if (pendingDelete.kind === 'team') {
        await api.delete(`/api/organizations/${organization.id}/sports/${pendingDelete.sportId}/teams/${pendingDelete.teamId}`);
        toast.success(`Deleted ${pendingDelete.label}`);
      } else {
        await api.delete(`/api/people/${pendingDelete.personId}/organizations/${pendingDelete.linkId}`);
        toast.success(`Unlinked ${pendingDelete.label}`);
      }
      await fetchOrganization(organization.id, { includeFormer: true });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to delete');
    } finally {
      setPendingDelete(null);
    }
  };

  const confirmCopy = (p: PendingDelete | null): { title: string; message: string; confirmLabel: string } => {
    if (p?.kind === 'coach') {
      return { title: 'Remove coach link', message: `Remove ${p.label} from this program? This unlinks the coach here — it doesn't delete the person.`, confirmLabel: 'Remove' };
    }
    if (p?.kind === 'team') {
      return { title: 'Delete team', message: `Delete "${p.label}"? It will be hidden from this org. You can restore it later if needed.`, confirmLabel: 'Delete' };
    }
    return { title: 'Delete sport', message: `Delete "${p?.label ?? ''}"? It will be hidden from this org. You can restore it later if needed.`, confirmLabel: 'Delete' };
  };

  const activeSports = useMemo(
    () => organization.sports.filter((s) => s.isActive),
    [organization.sports],
  );
  // Apply the empty-team filter BEFORE the name filter. When showEmptyTeams is
  // true, shownSports === activeSports.
  const shownSports = useMemo(
    () => visibleSports(activeSports, showEmptyTeams),
    [activeSports, showEmptyTeams],
  );
  const hidden = useMemo(() => hiddenCounts(activeSports), [activeSports]);
  const filteredSports = useMemo(
    () => filterSportsByQuery(shownSports, filterQuery),
    [shownSports, filterQuery],
  );
  // Header count = current real teams (active AND not empty), matching what each
  // SportCard shows by default.
  const activeTeamCount = shownSports.reduce(
    (sum, sport) => sum + sport.teams.filter((t) => t.isActive && !isHiddenTeam(t)).length,
    0,
  );

  const handleToggleSection = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    // Reset the filter every time the section closes — it's transient UI, not a saved preference.
    if (!next) setFilterQuery('');
  };

  const handleAddTeam = (sportId: string, sportDisplayName: string) => {
    setAddTeamSportId(sportId);
    setAddTeamSportName(sportDisplayName);
    setIsAddTeamOpen(true);
  };

  const handleAddFundraiser = (sportId: string) => {
    const sport = organization.sports.find(s => s.id === sportId);
    setAddFundraiserSportId(sportId);
    setAddFundraiserSportName(sport ? formatSportLabel(sport.gender, sport.name) : '');
    setIsAddFundraiserOpen(true);
  };

  const handleSportCreated = () => {
    fetchOrganization(organization.id, { includeFormer: true });
  };

  const handleTeamCreated = () => {
    fetchOrganization(organization.id, { includeFormer: true });
  };

  const trimmedFilter = filterQuery.trim();

  return (
    <div className="card overflow-hidden">
      {/* Section header — click to collapse/expand the whole section. */}
      <button
        type="button"
        onClick={handleToggleSection}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <Trophy className="h-5 w-5 text-blast-600 dark:text-blast-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            Sports &amp; Teams
          </h2>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
          {shownSports.length} {shownSports.length === 1 ? 'sport' : 'sports'}
          {' · '}
          {activeTeamCount} {activeTeamCount === 1 ? 'team' : 'teams'}
        </span>
      </button>

      {isExpanded && (
        <>
          {/* Filter + Add Sport row. On mobile, Add Sport stretches full-width with a
              taller tap target so iOS finger taps reliably hit it instead of the
              section-header toggle button immediately above. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 dark:border-gray-700 px-5 py-3 bg-gray-50 dark:bg-gray-800/30">
            {/* Filter input with inline Clear (X) button — pattern matches iOS/Safari search. */}
            <div className="relative w-full sm:max-w-xs">
              <input
                type="text"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="Filter sports…"
                className="input w-full pr-9"
              />
              {filterQuery && (
                <button
                  type="button"
                  onClick={() => setFilterQuery('')}
                  aria-label="Clear filter"
                  title="Clear filter"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {canEdit && (
              <button
                onClick={() => setIsAddSportOpen(true)}
                className="btn-primary text-sm py-2.5 sm:py-1.5 whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto sm:flex-shrink-0"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Sport
              </button>
            )}
          </div>

          {hidden.teams > 0 && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-2 bg-gray-50 dark:bg-gray-800/30">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showEmptyTeams}
                  onChange={(e) => setShowEmptyTeams(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blast-600 focus:ring-blast-500"
                />
                <span>
                  Show empty teams{' '}
                  <span className="text-gray-400 dark:text-gray-500">
                    ({hidden.sports > 0 ? `${hidden.sports} ${hidden.sports === 1 ? 'sport' : 'sports'}, ` : ''}
                    {hidden.teams} {hidden.teams === 1 ? 'team' : 'teams'} hidden)
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="p-5">
            {/* Blast summary bar (when linked) — counts computed from CRM-local data. */}
            {blastSchoolId && shownSports.length > 0 && (
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800/30">
                <Zap className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  Active in Blast
                </span>
                <span className="text-xs text-green-600 dark:text-green-500">
                  {shownSports.length} sports · {activeTeamCount} teams
                </span>
              </div>
            )}

            {activeSports.length === 0 ? (
              <div className="p-8 text-center">
                <Trophy className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto" />
                <p className="mt-2 text-gray-500 dark:text-gray-400">No sports programs yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Add a sport to start building the team hierarchy
                </p>
                {canEdit && (
                  <button
                    onClick={() => setIsAddSportOpen(true)}
                    className="btn-primary text-sm mt-4"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Sport
                  </button>
                )}
              </div>
            ) : shownSports.length === 0 ? (
              // Everything here is empty and hidden (showEmptyTeams is false in this branch).
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                Every team here is empty.{' '}
                <button
                  type="button"
                  onClick={() => setShowEmptyTeams(true)}
                  className="text-blast-600 hover:underline dark:text-blast-400"
                >
                  Show empty teams
                </button>
              </div>
            ) : filteredSports.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                No sports match &ldquo;{trimmedFilter}&rdquo;.
                {' '}
                <button
                  type="button"
                  onClick={() => setFilterQuery('')}
                  className="text-blast-600 hover:underline dark:text-blast-400"
                >
                  Clear filter
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSports.map((sport) => {
                  const orderedTeams = [...sport.teams].sort((a, b) => compareLevels(a.level, b.level));
                  const sportWithOrderedTeams = { ...sport, teams: orderedTeams };
                  const sportTeamIds = new Set(sport.teams.map((t) => t.id));
                  const sportBlastFundraisers = organization.blastFundraisers.filter(
                    (f) => f.teamId !== null && sportTeamIds.has(f.teamId),
                  );
                  const sportMunchFundCampaigns = (organization.munchFundCampaigns ?? []).filter(
                    (c) => c.teamId !== null && sportTeamIds.has(c.teamId),
                  );
                  return (
                    <SportCard
                      key={sport.id}
                      sport={sportWithOrderedTeams}
                      organizationId={organization.id}
                      onAddTeam={handleAddTeam}
                      onLinkPerson={canEdit && onLinkPerson ? (sportId, teamId) => onLinkPerson(sportId, teamId) : undefined}
                      fundraisers={orgFundraisers.filter(f => f.sportId === sport.id)}
                      blastFundraisers={sportBlastFundraisers}
                      munchFundCampaigns={sportMunchFundCampaigns}
                      onAddFundraiser={canEdit ? handleAddFundraiser : undefined}
                      onEditFundraiser={canEdit ? setEditingFundraiser : undefined}
                      onScheduleActivity={canEdit ? onScheduleActivity : undefined}
                      onConvert={canEdit ? onConvert : undefined}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onRequestDeleteSport={canDelete ? handleRequestDeleteSport : undefined}
                      onRequestDeleteTeam={canDelete ? handleRequestDeleteTeam : undefined}
                      onRequestUnlinkCoach={canDelete ? handleRequestUnlinkCoach : undefined}
                      showEmptyTeams={showEmptyTeams}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Mutation modals — only mounted when the viewer can edit. */}
      {canEdit && (
        <>
          <AddSportModal
            isOpen={isAddSportOpen}
            onClose={() => setIsAddSportOpen(false)}
            organizationId={organization.id}
            organizationName={organization.name}
            existingSports={organization.sports}
            onSuccess={handleSportCreated}
          />

          <AddTeamModal
            isOpen={isAddTeamOpen}
            onClose={() => setIsAddTeamOpen(false)}
            organizationId={organization.id}
            sportId={addTeamSportId}
            sportDisplayName={addTeamSportName}
            onSuccess={handleTeamCreated}
          />

          <AddFundraiserModal
            isOpen={isAddFundraiserOpen}
            onClose={() => setIsAddFundraiserOpen(false)}
            organizationId={organization.id}
            organizationName={organization.name}
            sportId={addFundraiserSportId!}
            sportDisplayName={addFundraiserSportName}
            sports={activeSports.map(({ id, name, gender }) => ({ id, name, gender }))}
            onSuccess={() => {
              fetchOrgFundraisers(organization.id);
              setIsAddFundraiserOpen(false);
            }}
          />

          <EditFundraiserModal
            fundraiser={editingFundraiser}
            isOpen={editingFundraiser !== null}
            onClose={() => setEditingFundraiser(null)}
            onSuccess={() => {
              setEditingFundraiser(null);
              fetchOrgFundraisers(organization.id);
            }}
            onScreenshotsChanged={() => fetchOrgFundraisers(organization.id)}
          />
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={confirmCopy(pendingDelete).title}
        message={confirmCopy(pendingDelete).message}
        confirmLabel={confirmCopy(pendingDelete).confirmLabel}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};
